import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { categories, categoryNames, movies } from "@/mocks/movies";
import { defaultFilters, filterMovies, getByCategory, paginate } from "@/utils/movies";
import type { FilterState } from "@/utils/movies";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/pages/category/components/FilterBar";

export default function CategoryPage() {
  const { type = "movie" } = useParams<{ type: string }>();
  const categoryName = categoryNames[type] ?? "影视";
  const categoryMeta = categories.find((item) => item.key === type);

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, [type]);

  useSEO(
    `${categoryName} - 色虎视频影视分类`,
    `色虎视频${categoryName}频道，聚合海量高清${categoryName}资源，支持按地区、年份、类型多维筛选与排序，帮你快速找到想看的作品。`,
  );

  const base = useMemo(() => getByCategory(movies, type), [type]);
  const filtered = useMemo(() => filterMovies(base, filters), [base, filters]);
  const { items, total, totalPages, page: safePage } = paginate(filtered, page);

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: categoryName }]} />

      <div className="mb-5 md:mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
          {categoryName}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500 md:text-base">
          {categoryMeta?.desc ?? `色虎视频${categoryName}频道，聚合海量高清资源。`}
        </p>
      </div>

      <div className="mb-6">
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleReset}
          total={total}
        />
      </div>

      {items.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {items.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} index={index} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={totalPages} onChange={handlePageChange} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-archive-line text-3xl" aria-hidden="true" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
            没有符合条件的内容
          </h2>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            当前筛选条件下暂无匹配的影视资源，试试调整地区、年份或类型，或直接重置筛选查看全部内容。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}